import { useQuery } from '@tanstack/react-query'
import { progressAPI } from '../../api/client'
import AppLayout from '../../components/layout/AppLayout'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

type MilestoneKey =
  | 'first_session' | 'streak_7' | 'streak_30'
  | 'vocab_50' | 'vocab_100' | 'writing_5' | 'speaking_5'
  | 'level_elementary' | 'level_intermediate'

const BADGE_META: Record<MilestoneKey, { label: string; icon: string; color: string; bg: string }> = {
  first_session:      { label: 'Phiên đầu tiên',    icon: 'flag',                color: '#004be2', bg: '#eef0ff' },
  streak_7:           { label: 'Chuỗi 7 ngày',       icon: 'local_fire_department', color: '#b45309', bg: '#fef3c7' },
  streak_30:          { label: 'Chuỗi 30 ngày',      icon: 'whatshot',            color: '#b41340', bg: '#ffe8ed' },
  vocab_50:           { label: '50 từ vựng',          icon: 'library_books',       color: '#8e00d5', bg: '#f5e6ff' },
  vocab_100:          { label: '100 từ vựng',         icon: 'auto_stories',        color: '#8e00d5', bg: '#f5e6ff' },
  writing_5:          { label: '5 bài viết',          icon: 'edit_note',           color: '#8e00d5', bg: '#f5e6ff' },
  speaking_5:         { label: '5 bài nói',           icon: 'mic',                 color: '#b41340', bg: '#ffe8ed' },
  level_elementary:   { label: 'Cấp Elementary',      icon: 'star',                color: '#b45309', bg: '#fef3c7' },
  level_intermediate: { label: 'Cấp Intermediate',    icon: 'diamond',             color: '#004be2', bg: '#eef0ff' },
}

const SKILL_COLORS: Record<string, { color: string; bg: string }> = {
  listening: { color: '#006571', bg: '#e6fafb' },
  reading:   { color: '#004be2', bg: '#e8eeff' },
  writing:   { color: '#8e00d5', bg: '#f5e6ff' },
  speaking:  { color: '#b41340', bg: '#ffe8ed' },
}

const SKILL_LABELS: Record<string, string> = {
  listening: 'Nghe', reading: 'Đọc', writing: 'Viết', speaking: 'Nói',
}

const CEFR_NODES = [
  { level: 'A1', index: 0 },
  { level: 'A2', index: 1 },
  { level: 'B1', index: 2 },
  { level: 'B2', index: 3 },
  { level: 'C1', index: 4 },
  { level: 'C2', index: 5 },
]

// Derive current CEFR level from skill scores (avg → nearest CEFR node)
function scoreToCEFR(avg: number): string {
  if (avg >= 9) return 'C1'
  if (avg >= 7.5) return 'B2'
  if (avg >= 5) return 'B1'
  if (avg >= 2) return 'A2'
  return 'A1'
}

/** FR-012 + FR-014 — Progress & Analytics */
export default function ProgressPage() {
  const { data, isLoading } = useQuery({ queryKey: ['dashboard'], queryFn: progressAPI.dashboard })

  return (
    <AppLayout>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <span className="material-symbols-outlined" style={{ color: '#004be2', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>leaderboard</span>
          <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#004be2' }}>Tiến độ học tập</span>
        </div>
        <H style={{ display: 'block', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '0.5rem' }}>
          Hành trình <span style={{ color: '#004be2', fontStyle: 'italic' }}>của bạn</span>
        </H>
        <p style={{ color: '#535b71', fontSize: '1rem', lineHeight: 1.6 }}>
          Theo dõi XP, chuỗi ngày học và thành tích của bạn.
        </p>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <div className="spinner" />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {/* Streak + Vocab summary row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))', gap: '1.5rem' }}>
            <StreakCard streak={data?.streak} />
            <VocabCard vocabulary={data?.vocabulary} />
          </div>

          {/* XP + CEFR */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))', gap: '1.5rem' }}>
            {/* XP card */}
            {data?.xp && <XPLevelCard xp={data.xp} />}

            {/* CEFR Milestone nodes */}
            <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 10px 30px rgba(0,75,226,0.06)' }}>
              <H style={{ display: 'block', fontSize: '1.125rem', fontWeight: 800, marginBottom: '0.25rem' }}>Lộ trình CEFR</H>
              <span style={{ fontSize: '0.8125rem', color: '#535b71', display: 'block', marginBottom: '1.75rem' }}>Tiến độ theo chuẩn quốc tế</span>

              {(() => {
                // Use forecastLevel if available, otherwise derive from skill scores.
                // If user hasn't taken any test → currentLevel stays null and we show a CTA.
                const skills = data?.learningProfile?.skills
                const skillValues = skills ? (Object.values(skills) as number[]).filter(Boolean) : []
                const avgScore = skillValues.length > 0
                  ? skillValues.reduce((a, b) => a + b, 0) / skillValues.length
                  : 0
                const forecastLevel = data?.learningProfile?.forecastLevel ?? null
                const derivedLevel = avgScore > 0 ? scoreToCEFR(avgScore) : null
                const currentLevel = forecastLevel ?? derivedLevel
                const targetLevel = data?.learningProfile?.targetLevel ?? null
                const hasData = !!currentLevel || !!targetLevel
                const currentIdx = currentLevel ? CEFR_NODES.findIndex(n => n.level === currentLevel) : -1
                const targetIdx = targetLevel ? CEFR_NODES.findIndex(n => n.level === targetLevel) : -1
                // progress % based on current position
                const progressPct = currentIdx >= 0
                  ? ((currentIdx / (CEFR_NODES.length - 1)) * 100)
                  : 0

                if (!hasData) {
                  return (
                    <div style={{ padding: '1.5rem 0', textAlign: 'center' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '2rem', color: '#a5adc6', display: 'inline-block', marginBottom: '0.5rem' }}>quiz</span>
                      <p style={{ fontSize: '0.875rem', color: '#535b71', marginBottom: '1rem' }}>
                        Bạn chưa làm bài đánh giá đầu vào. Hoàn tất để xem trình độ CEFR hiện tại.
                      </p>
                      <a href="/onboarding" style={{ display: 'inline-block', padding: '0.625rem 1.5rem', borderRadius: '9999px', background: '#004be2', color: 'white', fontWeight: 700, fontSize: '0.8125rem', textDecoration: 'none' }}>
                        Làm bài đánh giá
                      </a>
                    </div>
                  )
                }

                return (
                  <div style={{ padding: '0.75rem 0 1.25rem' }}>
                    {/* Track container */}
                    <div style={{ position: 'relative', height: '3.5rem', display: 'flex', alignItems: 'center' }}>
                      {/* Base track */}
                      <div style={{ position: 'absolute', left: 0, right: 0, height: '0.1875rem', background: '#d1dcff', borderRadius: '9999px' }} />
                      {/* Progress track */}
                      <div style={{ position: 'absolute', left: 0, width: `${progressPct}%`, height: '0.1875rem', background: 'linear-gradient(90deg, #004be2, #809bff)', borderRadius: '9999px', transition: 'width 0.6s ease' }} />

                      {/* Nodes */}
                      <div style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {CEFR_NODES.map((node, i) => {
                          const done = i < currentIdx
                          const active = i === currentIdx
                          const isTarget = i === targetIdx && targetIdx !== currentIdx
                          return (
                            <div key={node.level} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.375rem' }}>
                              <div style={{
                                width: active ? '3rem' : '2.25rem',
                                height: active ? '3rem' : '2.25rem',
                                borderRadius: '50%',
                                background: done || active ? 'linear-gradient(135deg, #004be2, #809bff)' : isTarget ? '#fff7ed' : 'white',
                                border: done || active ? 'none' : isTarget ? '2px dashed #b45309' : '2px solid #d1dcff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: done || active ? 'white' : isTarget ? '#b45309' : '#a5adc6',
                                fontFamily: "'Space Grotesk', sans-serif", fontWeight: 900,
                                fontSize: active ? '0.875rem' : '0.6875rem',
                                boxShadow: active ? '0 0 0 5px rgba(0,75,226,0.12), 0 4px 16px rgba(0,75,226,0.35)' : done ? '0 2px 8px rgba(0,75,226,0.2)' : 'none',
                                transition: 'all 0.3s',
                                flexShrink: 0,
                              }}>
                                {done
                                  ? <span className="material-symbols-outlined" style={{ fontSize: '0.875rem', fontVariationSettings: "'FILL' 1" }}>check</span>
                                  : node.level}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>

                    {/* Labels row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem' }}>
                      {CEFR_NODES.map((node, i) => {
                        const active = i === currentIdx
                        const isTarget = i === targetIdx && targetIdx !== currentIdx
                        return (
                          <span key={node.level} style={{
                            fontSize: '0.625rem', fontWeight: 800,
                            color: active ? '#004be2' : isTarget ? '#b45309' : '#a5adc6',
                            textTransform: 'uppercase', letterSpacing: '0.06em',
                            textAlign: 'center', width: '3rem', marginLeft: i === 0 ? '-0.375rem' : i === CEFR_NODES.length - 1 ? '-0.375rem' : undefined,
                          }}>
                            {active ? 'Hiện tại' : isTarget ? 'Mục tiêu' : node.level}
                          </span>
                        )
                      })}
                    </div>
                  </div>
                )
              })()}
            </div>
          </div>

          {/* Skill breakdown */}
          <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 10px 30px rgba(0,75,226,0.06)' }}>
            <H style={{ display: 'block', fontSize: '1.125rem', fontWeight: 800, marginBottom: '0.25rem' }}>Điểm theo kỹ năng</H>
            <span style={{ fontSize: '0.8125rem', color: '#535b71', display: 'block', marginBottom: '2rem' }}>Dựa trên kết quả các bài thi thử và luyện tập</span>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))', gap: '1.5rem' }}>
              {Object.entries(SKILL_COLORS).map(([skill, cfg]) => {
                const score = data?.learningProfile?.skills?.[skill] ?? 0
                const pct = Math.min(100, (score / 10) * 100)
                return (
                  <div key={skill}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.625rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                        <div style={{ width: '2.25rem', height: '2.25rem', borderRadius: '0.625rem', background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span className="material-symbols-outlined" style={{ color: cfg.color, fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>
                            {skill === 'listening' ? 'headphones' : skill === 'reading' ? 'menu_book' : skill === 'writing' ? 'edit_note' : 'mic'}
                          </span>
                        </div>
                        <span style={{ fontWeight: 700, fontSize: '0.9375rem' }}>{SKILL_LABELS[skill]}</span>
                      </div>
                      <H style={{ fontWeight: 900, color: cfg.color }}>{score ? score.toFixed(1) : '—'}<span style={{ fontWeight: 400, color: '#a5adc6', fontSize: '0.8125rem' }}>/10</span></H>
                    </div>
                    <div style={{ height: '0.5rem', background: '#eef0ff', borderRadius: '9999px', overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: cfg.color, borderRadius: '9999px', transition: 'width 0.6s' }} />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Weekly XP chart */}
          {data?.recentSessions && data.recentSessions.length > 0 && (
            <SkillHistoryChart sessions={data.recentSessions} />
          )}

          {/* Milestones */}
          {data?.milestones && data.milestones.length > 0 && (
            <MilestoneBadges milestones={data.milestones} />
          )}
        </div>
      )}
    </AppLayout>
  )
}

// ─── Streak Card ─────────────────────────────────────────────────────────────

function StreakCard({ streak }: { streak: any }) {
  const current = streak?.current ?? 0
  const longest = streak?.longest ?? 0
  const freezeCount = streak?.freezeCount ?? 0

  return (
    <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 10px 30px rgba(0,75,226,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
        <span className="material-symbols-outlined" style={{ color: '#b45309', fontSize: '1.25rem', fontVariationSettings: "'FILL' 1" }}>local_fire_department</span>
        <H style={{ fontSize: '1rem', fontWeight: 800 }}>Chuỗi ngày học</H>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', textAlign: 'center' }}>
        <div>
          <H style={{ display: 'block', fontSize: '2rem', fontWeight: 900, color: '#b45309', lineHeight: 1 }}>{current}</H>
          <span style={{ fontSize: '0.75rem', color: '#535b71', fontWeight: 600 }}>Hiện tại</span>
        </div>
        <div>
          <H style={{ display: 'block', fontSize: '2rem', fontWeight: 900, color: '#004be2', lineHeight: 1 }}>{longest}</H>
          <span style={{ fontSize: '0.75rem', color: '#535b71', fontWeight: 600 }}>Dài nhất</span>
        </div>
        <div>
          <H style={{ display: 'block', fontSize: '2rem', fontWeight: 900, color: '#8e00d5', lineHeight: 1 }}>{freezeCount}</H>
          <span style={{ fontSize: '0.75rem', color: '#535b71', fontWeight: 600 }}>Lượt freeze</span>
        </div>
      </div>
      {!streak && (
        <p style={{ textAlign: 'center', color: '#a5adc6', fontSize: '0.875rem', marginTop: '1rem' }}>Chưa có dữ liệu streak</p>
      )}
    </div>
  )
}

// ─── Vocab Card ───────────────────────────────────────────────────────────────

function VocabCard({ vocabulary }: { vocabulary: any }) {
  const total = vocabulary?.total ?? 0
  const dueToday = vocabulary?.dueToday ?? 0

  return (
    <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 10px 30px rgba(0,75,226,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
        <span className="material-symbols-outlined" style={{ color: '#8e00d5', fontSize: '1.25rem', fontVariationSettings: "'FILL' 1" }}>library_books</span>
        <H style={{ fontSize: '1rem', fontWeight: 800 }}>Từ vựng</H>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', textAlign: 'center' }}>
        <div>
          <H style={{ display: 'block', fontSize: '2rem', fontWeight: 900, color: '#8e00d5', lineHeight: 1 }}>{total}</H>
          <span style={{ fontSize: '0.75rem', color: '#535b71', fontWeight: 600 }}>Tổng từ đã học</span>
        </div>
        <div style={{ position: 'relative' }}>
          <H style={{ display: 'block', fontSize: '2rem', fontWeight: 900, color: dueToday > 0 ? '#b41340' : '#22c55e', lineHeight: 1 }}>{dueToday}</H>
          <span style={{ fontSize: '0.75rem', color: '#535b71', fontWeight: 600 }}>Cần ôn hôm nay</span>
          {dueToday > 0 && (
            <div style={{ marginTop: '0.5rem' }}>
              <span style={{ fontSize: '0.6875rem', background: '#ffe8ed', color: '#b41340', padding: '0.125rem 0.5rem', borderRadius: '9999px', fontWeight: 700 }}>Cần ôn tập!</span>
            </div>
          )}
        </div>
      </div>
      {!vocabulary && (
        <p style={{ textAlign: 'center', color: '#a5adc6', fontSize: '0.875rem', marginTop: '1rem' }}>Chưa có từ vựng nào</p>
      )}
    </div>
  )
}

// ─── XP Level Card ────────────────────────────────────────────────────────────

function XPLevelCard({ xp }: { xp: any }) {
  const pct = xp.xpForNextLevel > 0
    ? Math.min(100, Math.round((xp.xpIntoLevel / xp.xpForNextLevel) * 100))
    : 100

  return (
    <div style={{ background: 'linear-gradient(135deg, #004be2, #809bff)', borderRadius: '1.5rem', padding: '2rem', color: 'white', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', bottom: '-2rem', right: '-2rem', width: '10rem', height: '10rem', background: 'rgba(142,0,213,0.3)', borderRadius: '50%', filter: 'blur(40px)' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', position: 'relative' }}>
        <div>
          <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '0.25rem' }}>Level hiện tại</span>
          <H style={{ fontSize: '2.5rem', fontWeight: 900, color: 'white', lineHeight: 1 }}>{xp.level}</H>
        </div>
        <div style={{ textAlign: 'right' }}>
          <span style={{ display: 'block', fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginBottom: '0.25rem' }}>Tuần này</span>
          <H style={{ fontSize: '1.5rem', fontWeight: 900, color: 'white' }}>{xp.weeklyXP} <span style={{ fontSize: '0.875rem', opacity: 0.7 }}>XP</span></H>
        </div>
      </div>

      <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>XP đến level tiếp theo</span>
          <span style={{ fontSize: '0.75rem', color: 'white', fontWeight: 700 }}>{pct}%</span>
        </div>
        <div style={{ width: '100%', height: '0.5rem', background: 'rgba(255,255,255,0.2)', borderRadius: '9999px', overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: 'white', borderRadius: '9999px', transition: 'width 0.6s' }} />
        </div>
        <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>
          {xp.xpIntoLevel} / {xp.xpForNextLevel || '∞'} XP
        </p>
      </div>
    </div>
  )
}

// ─── Skill History Chart ──────────────────────────────────────────────────────

function SkillHistoryChart({ sessions }: { sessions: any[] }) {
  const skills = ['listening', 'reading', 'writing', 'speaking']
  // Backend returns: [session_date, skill_focus, duration_seconds, xp_earned, completed_at]
  // Map to named fields for safety
  const mapped = sessions.map(s => Array.isArray(s)
    ? { skillFocus: s[1], xpEarned: s[3] ?? 0 }
    : { skillFocus: s.skill_focus ?? s.skillFocus, xpEarned: s.xp_earned ?? s.xpEarned ?? 0 }
  )
  const totals = Object.fromEntries(
    skills.map(sk => [sk, mapped.filter(s => s.skillFocus === sk).reduce((sum, s) => sum + s.xpEarned, 0)])
  )
  const max = Math.max(...Object.values(totals) as number[], 1)

  return (
    <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 10px 30px rgba(0,75,226,0.06)' }}>
      <H style={{ display: 'block', fontSize: '1.125rem', fontWeight: 800, marginBottom: '0.25rem' }}>XP theo kỹ năng (7 ngày)</H>
      <span style={{ fontSize: '0.8125rem', color: '#535b71', display: 'block', marginBottom: '2rem' }}>Tổng XP kiếm được theo từng kỹ năng</span>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', height: '10rem', alignItems: 'flex-end' }}>
        {skills.map(sk => {
          const cfg = SKILL_COLORS[sk]
          const heightPct = Math.max(4, Math.round(((totals[sk] as number) / max) * 100))
          return (
            <div key={sk} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', height: '100%', justifyContent: 'flex-end' }}>
              <H style={{ fontSize: '0.875rem', fontWeight: 700, color: cfg.color }}>{totals[sk]}</H>
              <div style={{ width: '100%', borderRadius: '0.75rem 0.75rem 0 0', background: cfg.bg, height: `${heightPct}%`, transition: 'height 0.5s', cursor: 'pointer', position: 'relative' }}
                onMouseEnter={e => { e.currentTarget.style.background = cfg.color }}
                onMouseLeave={e => { e.currentTarget.style.background = cfg.bg }}
              />
              <span style={{ fontSize: '0.6875rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#535b71' }}>{SKILL_LABELS[sk]}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Milestone Badges ─────────────────────────────────────────────────────────

function MilestoneBadges({ milestones }: { milestones: any[] }) {
  return (
    <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 10px 30px rgba(0,75,226,0.06)' }}>
      <H style={{ display: 'block', fontSize: '1.125rem', fontWeight: 800, marginBottom: '0.25rem' }}>Thành tựu</H>
      <span style={{ fontSize: '0.8125rem', color: '#535b71', display: 'block', marginBottom: '2rem' }}>Huy hiệu đã đạt được trong hành trình học tập</span>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(9rem, 1fr))', gap: '1rem' }}>
        {milestones.map((m: any) => {
          const meta = BADGE_META[m.key as MilestoneKey]
          if (!meta) return null
          const earned = !!m.earnedAt
          return (
            <div
              key={m.key}
              style={{
                padding: '1.25rem', borderRadius: '1.25rem', textAlign: 'center',
                background: earned ? meta.bg : '#f8f9fe',
                border: `1.5px solid ${earned ? `${meta.color}20` : '#eef0ff'}`,
                opacity: earned ? 1 : 0.5,
                transition: 'all 0.2s',
              }}
            >
              <div style={{ width: '3rem', height: '3rem', borderRadius: '50%', background: earned ? meta.bg : '#eef0ff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.75rem' }}>
                <span className="material-symbols-outlined" style={{ color: earned ? meta.color : '#a5adc6', fontSize: '1.5rem', fontVariationSettings: earned ? "'FILL' 1" : "'FILL' 0" }}>{meta.icon}</span>
              </div>
              <H style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 800, color: earned ? '#272e42' : '#a5adc6', marginBottom: '0.25rem' }}>{meta.label}</H>
              {earned && m.earnedAt && (
                <span style={{ fontSize: '0.625rem', color: '#a5adc6', fontWeight: 600 }}>
                  {new Date(m.earnedAt).toLocaleDateString('vi-VN')}
                </span>
              )}
              {!earned && <span style={{ fontSize: '0.625rem', color: '#a5adc6' }}>Chưa đạt</span>}
            </div>
          )
        })}
      </div>
    </div>
  )
}
